import { Card, DatePicker, Form, Input, Select, Space, Switch, Typography } from 'antd'
import { ReactNode } from 'react'
import { Dayjs } from 'dayjs'
import { jobNameMaxLength } from '../../domain/jobs/jobName'
import { jobNumberMaxLength } from './jobNumberModel'
import { clockOutRequirementOptions, statusLabel, statusOptions } from './jobDetailsFormModel'
import LabeledSwitch from '../common/LabeledSwitch'
import VSpace from '../common/VSpace'
import SchedulerSwitches from './SchedulerSwitches'

type Option<T> = { label: string, value: T }

/** Completed Date is part of the page's job and saves with its Save Changes button, not the details autosave. */
export type CompletedDateField = {
  value: Dayjs | null
  onChange: (date: Dayjs | null) => void
}

type Props = {
  jobNumberRequired: boolean
  isProject: boolean
  isMaintenanceOrTicket: boolean
  sold: boolean
  userOptions: Option<string>[]
  budgetYearOptions: Option<number>[]
  onLostChange: (checked: boolean) => void
  onSoldChange: (checked: boolean) => void
  jobOperations: ReactNode
  pricing?: ReactNode
  completedDate?: CompletedDateField
}

const clockOutRequirementLabel = ({ label, value }: { label?: ReactNode, value?: string | number }) =>
  value === 'companyDefault' ? <Typography.Text type="secondary">{label}</Typography.Text> : label

// The redesigned job Details tab (job-details-cleanup). Rendered inside JobDetailsForm's Form, so the
// Form.Items here share its instance, autosave and validation.
const JobDetailsSections = ({
  jobNumberRequired, isProject, isMaintenanceOrTicket, sold, userOptions, budgetYearOptions,
  onLostChange, onSoldChange, jobOperations, pricing, completedDate,
}: Props) =>
  <VSpace size="middle">
    <Card title="Job Details" extra={jobOperations}>
      {!isProject && <Form.Item name="estimateDate" hidden><input type="hidden" /></Form.Item>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 16px' }}>
        <Form.Item name="name" label="Name" style={{ gridColumn: 'span 3' }}
          rules={[{ required: true, whitespace: true, message: 'A job name is required.' }]}>
          <Input maxLength={jobNameMaxLength} />
        </Form.Item>
        <Form.Item name="no" label="Number"
          rules={jobNumberRequired ? [{ required: true, whitespace: true, message: 'A job number is required.' }] : []}>
          <Input maxLength={jobNumberMaxLength} placeholder="Pending" />
        </Form.Item>
        {isProject && <Form.Item name="estimateDate" label="Estimate Date">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>}
        <Form.Item name="salesPersonEmail" label="Salesperson">
          <Select options={userOptions} allowClear />
        </Form.Item>
        {isProject && <Form.Item name="estimatorEmail" label="Estimator">
          <Select options={userOptions} allowClear />
        </Form.Item>}
        <Form.Item name="budgetYear" label="MORS Budget">
          <Select options={budgetYearOptions} />
        </Form.Item>
      </div>
    </Card>

    <Card title="Status">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 32px' }}>
        <Form.Item label="Outcome">
          <Space size="large">
            <Form.Item name="lost" noStyle>
              <LabeledSwitch label="Lost" aria-label="Lost" onChange={onLostChange} />
            </Form.Item>
            <Form.Item name="sold" noStyle>
              <LabeledSwitch label="Sold" aria-label="Sold" onChange={onSoldChange} />
            </Form.Item>
          </Space>
        </Form.Item>
        {sold && <Form.Item name="soldOn" label="Sold Date">
          <DatePicker format="MM/DD/YYYY" allowClear={false} />
        </Form.Item>}
        {isProject && sold && <Form.Item name="status" label="Status">
          <Select style={{ minWidth: 200 }} options={statusOptions} labelRender={statusLabel} />
        </Form.Item>}
        {completedDate && <Form.Item label="Completed Date" htmlFor="completedDate" style={{ width: 160 }}>
          <DatePicker id="completedDate" style={{ width: '100%' }} value={completedDate.value} onChange={completedDate.onChange} />
        </Form.Item>}
        {isMaintenanceOrTicket && <Form.Item name="active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>}
      </div>
    </Card>

    {pricing && <Card title="Pricing">
    {pricing}
  </Card>}

  <Card title="Scheduling">
      <Form.Item label="Notify Schedulers">
        <Typography.Paragraph type="secondary">
          A notification email is sent to each selected user when this job is marked as sold.
        </Typography.Paragraph>
        <Form.Item name="schedulers" noStyle>
          <SchedulerSwitches options={userOptions} />
        </Form.Item>
      </Form.Item>
      <Form.Item name="schedulingNotes" label="Scheduling Notes"
        tooltip="Scheduling notes are copied into each visit's notes when this job is scheduled, which crews see in the mobile app. Edits made later don't change visits that are already scheduled. They also show on the Schedule page, print on the crew day-schedule PDF, and are included in the email sent to the selected schedulers when this job is marked as sold.">
        <Input.TextArea rows={3} />
      </Form.Item>
    </Card>

    <Card title="Crew">
      <Form.Item name="crewNotes" label="Crew Notes"
        tooltip="Crew notes show up on the printed job report and in the email sent to the selected schedulers when this job is marked as sold. They are not shown in the mobile app.">
        <Input.TextArea rows={4} />
      </Form.Item>
      <Form.Item label="Required at Clock-Out" style={{ marginBottom: 0 }}
        tooltip="Before finishing a visit on this job, crews are asked in the mobile app for an internal note and/or a photo. Company default follows the setting in My Company.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 16px' }}>
          <Form.Item name="clockOutNotes" label={<Typography.Text type="secondary">Internal note</Typography.Text>}>
            <Select options={clockOutRequirementOptions} labelRender={clockOutRequirementLabel} />
          </Form.Item>
          <Form.Item name="clockOutPhoto" label={<Typography.Text type="secondary">Photo</Typography.Text>}>
            <Select options={clockOutRequirementOptions} labelRender={clockOutRequirementLabel} />
          </Form.Item>
        </div>
      </Form.Item>
    </Card>
  </VSpace>

export default JobDetailsSections
